// ─── Mobile Layout Health Check ───────────────────────────────
// Phase 1.7. Owner/admin-only, on-demand. Runs against the LIVE public
// invitation rendered in an off-screen iframe — no mutation of the
// invitation data, no impact on the public render path.
//
// Severity:
//   error   → blocks "Looks good on mobile" verdict
//   warning → advisory, listed in results
//   info    → quiet hint
//
// All checks are individually try/catched so one broken check doesn't
// hide the others.

const SEVERITY = { ERROR: 'error', WARNING: 'warning', INFO: 'info' };

function safe(fn, fallback) {
  try { return fn(); } catch { return fallback; }
}

export function findNestedScrollTraps(doc) {
  return safe(() => {
    const root = doc.querySelector('.public-invite-page') || doc.body;
    if (!root) return [];
    const traps = [];
    root.querySelectorAll('*').forEach((el) => {
      const cs = doc.defaultView.getComputedStyle(el);
      if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
          el.scrollHeight > el.clientHeight + 8) {
        traps.push(el);
      }
    });
    return traps;
  }, []);
}

export function findFixedBackgrounds(doc) {
  return safe(() => {
    const root = doc.querySelector('.public-invite-page') || doc.body;
    if (!root) return [];
    const out = [];
    root.querySelectorAll('*').forEach((el) => {
      const cs = doc.defaultView.getComputedStyle(el);
      if (cs.position === 'fixed') {
        const r = el.getBoundingClientRect();
        // Only flag "background-like" — large, behind content, no pointer events.
        if (r.width >= 320 && r.height >= 400) {
          out.push(el);
        }
      }
    });
    return out;
  }, []);
}

export function findHeavyImages(doc) {
  return safe(() => {
    const root = doc.querySelector('.public-invite-page') || doc.body;
    if (!root) return [];
    return Array.from(root.querySelectorAll('img')).filter((img) => {
      const pixels = (img.naturalWidth || 0) * (img.naturalHeight || 0);
      // Threshold sits above the mobile-optimize ceiling (2.0MP target +
      // a small headroom). Anything genuinely too heavy (>2.7MP) still
      // trips; freshly optimized images do not.
      return pixels > 2_700_000;
    });
  }, []);
}

export function findOutOfBoundsElements(doc) {
  return safe(() => {
    const root = doc.querySelector('.public-invite-page') || doc.body;
    if (!root) return [];
    const rootRect = root.getBoundingClientRect();
    const out = [];
    root.querySelectorAll('[data-testid^="v1-el-"], [data-testid^="collage-el-"]').forEach((el) => {
      const r = el.getBoundingClientRect();
      // Per-side overflow (positive number = px past that edge).
      const overflowLeft = Math.max(0, Math.round(rootRect.left - r.left));
      const overflowRight = Math.max(0, Math.round(r.right - rootRect.right));
      const overflowTop = Math.max(0, Math.round(rootRect.top - r.top));
      const overflowBottom = Math.max(0, Math.round(r.bottom - rootRect.bottom));
      // We only flag horizontal overflow today (matches the prior
      // detection). Vertical is captured but not used as a trigger.
      if (overflowRight > 2 || overflowLeft > 2) {
        // Element identity. Renderer emits data-testid="v1-el-<id>";
        // section-v1 canvas also adds data-section-id and friendly
        // labels so the editor can select the right element on click.
        const testid = el.getAttribute('data-testid') || '';
        const id = el.getAttribute('data-element-id')
          || testid.replace(/^v1-el-|^collage-el-/, '')
          || null;
        const sectionId = el.getAttribute('data-section-id') || null;
        const type = el.getAttribute('data-element-type')
          || (el.tagName === 'IMG' || el.querySelector('img') ? 'image'
            : (el.querySelector('h1,h2,h3,h4,p,span') ? 'text' : 'element'));
        const name = el.getAttribute('data-element-name')
          || el.getAttribute('alt')
          || (el.querySelector('img')?.getAttribute('alt'))
          || null;
        // Direction of largest overflow drives the message.
        const direction =
          overflowRight >= overflowLeft && overflowRight > 0 ? 'right'
            : overflowLeft > 0 ? 'left'
              : overflowBottom > overflowTop ? 'bottom' : 'top';
        const overflowPx = Math.max(overflowLeft, overflowRight, overflowTop, overflowBottom);
        // "Full-width with horizontal drift" — width is essentially the
        // full canvas but the element has slipped left/right. This is the
        // classic stale-x-after-Full-preset case; the modal will surface a
        // one-click "Snap to canvas edges" action for this subtype.
        const widthPct = rootRect.width > 0 ? (r.width / rootRect.width) * 100 : 0;
        const isFullWidthDrift = widthPct >= 95 && (overflowLeft > 2 || overflowRight > 2);
        out.push({
          el,
          id,
          sectionId,
          type,
          name,
          direction,
          overflowPx,
          isFullWidthDrift,
          widthPct: +widthPct.toFixed(1),
          bounds: {
            left: Math.round(r.left - rootRect.left),
            right: Math.round(r.right - rootRect.left),
            top: Math.round(r.top - rootRect.top),
            bottom: Math.round(r.bottom - rootRect.top),
            width: Math.round(r.width),
            height: Math.round(r.height),
          },
          canvas: {
            width: Math.round(rootRect.width),
            height: Math.round(rootRect.height),
          },
        });
      }
    });
    return out;
  }, []);
}

export function findImagesMissingDimensions(doc) {
  return safe(() => {
    const root = doc.querySelector('.public-invite-page') || doc.body;
    if (!root) return [];
    return Array.from(root.querySelectorAll('img')).filter((img) => {
      const hasWidth = img.hasAttribute('width') || img.style.width;
      const hasHeight = img.hasAttribute('height') || img.style.height || img.style.aspectRatio;
      return !hasWidth || !hasHeight;
    });
  }, []);
}

export function findNonLazyImages(doc) {
  return safe(() => {
    const root = doc.querySelector('.public-invite-page') || doc.body;
    if (!root) return [];
    const imgs = Array.from(root.querySelectorAll('img'));
    // First image is usually the LCP hero — exclude.
    return imgs.slice(1).filter((img) => img.loading !== 'lazy');
  }, []);
}

export function detectHorizontalOverflow(doc) {
  return safe(() => {
    const w = doc.defaultView;
    return doc.documentElement.scrollWidth > (w.innerWidth || 390) + 1;
  }, false);
}

/**
 * Main entry point.
 * @param {Document} iframeDoc — document of the off-screen public-preview iframe
 * @param {string}   layoutVersion — from rsvp_page meta (passed in by caller)
 * @returns {{ ok, summary, issues }}
 */
export function runMobileLayoutHealthCheck({ iframeDoc, layoutVersion, layoutMode }) {
  const issues = [];

  // 1. Renderer
  if (layoutVersion !== 'section-v1') {
    issues.push({
      severity: SEVERITY.WARNING,
      code: 'not_section_v1',
      message: 'This invitation is not using the mobile-optimized renderer (section-v1). Enable it from the Invitation tab for better mobile parity.',
    });
  }

  if (!iframeDoc) {
    issues.push({
      severity: SEVERITY.WARNING,
      code: 'no_dom',
      message: "Couldn't load the live preview to verify. Check that the invitation is published or has a preview link.",
    });
    return { ok: false, summary: summarize(issues, layoutVersion, layoutMode), issues };
  }

  // 2. Horizontal overflow
  if (detectHorizontalOverflow(iframeDoc)) {
    issues.push({
      severity: SEVERITY.ERROR,
      code: 'horizontal_overflow',
      message: 'Horizontal overflow detected on mobile — guests may see a sideways scrollbar.',
    });
  }

  // 3. Nested scroll containers
  const traps = findNestedScrollTraps(iframeDoc);
  if (traps.length > 0) {
    issues.push({
      severity: SEVERITY.ERROR,
      code: 'nested_scroll_trap',
      message: `${traps.length} nested scroll container${traps.length > 1 ? 's' : ''} detected. These cause scroll to feel stuck on iPhone.`,
    });
  }

  // 4. Fixed full-screen backgrounds on mobile
  const fixedBgs = findFixedBackgrounds(iframeDoc);
  if (fixedBgs.length > 0) {
    issues.push({
      severity: SEVERITY.WARNING,
      code: 'fixed_background_mobile',
      message: `${fixedBgs.length} fixed full-screen element${fixedBgs.length > 1 ? 's' : ''} detected. Fixed backgrounds can cause scroll jank on iPhone.`,
    });
  }

  // 5. Heavy images
  const heavy = findHeavyImages(iframeDoc);
  if (heavy.length > 0) {
    issues.push({
      severity: SEVERITY.WARNING,
      code: 'heavy_image',
      message: `${heavy.length} large image${heavy.length > 1 ? 's' : ''} detected (over 2.7MP). Consider optimizing for mobile.`,
      // Action context — emit ALL heavy image URLs. The backend's
      // /optimize endpoint enforces "owned by event" via GridFS metadata,
      // so we don't need to filter here. External URLs come back under
      // `failed[]` with a clear reason the modal can surface.
      images: heavy.map((img) => ({
        src: img.currentSrc || img.src,
        naturalWidth: img.naturalWidth || 0,
        naturalHeight: img.naturalHeight || 0,
      })).filter((it) => !!it.src),
    });
  }

  // 6. Elements outside canvas
  const oob = findOutOfBoundsElements(iframeDoc);
  if (oob.length > 0) {
    // Compose a friendly message for the FIRST offender; multi-offender
    // case keeps the higher-level count for at-a-glance scanning.
    const first = oob[0];
    const friendlyType = first.type ? first.type.charAt(0).toUpperCase() + first.type.slice(1) : 'Element';
    const namePart = first.name ? ` "${first.name}"` : '';
    const detail = `${friendlyType}${namePart} extends ${first.overflowPx}px past the ${first.direction} edge.`;
    const message = oob.length === 1
      ? `1 element appears outside the mobile canvas — ${detail}`
      : `${oob.length} elements appear outside the mobile canvas. ${detail}`;
    issues.push({
      severity: SEVERITY.WARNING,
      code: 'element_out_of_bounds',
      message,
      // Action context — strip the DOM `el` reference (not serializable
      // across iframe boundaries cleanly) but keep IDs the editor can
      // use to locate the offending element on Select.
      elements: oob.map((o) => ({
        id: o.id,
        sectionId: o.sectionId,
        type: o.type,
        name: o.name,
        direction: o.direction,
        overflowPx: o.overflowPx,
        bounds: o.bounds,
        canvas: o.canvas,
        isFullWidthDrift: o.isFullWidthDrift,
        widthPct: o.widthPct,
      })),
    });
  }

  // 7. Images missing dimensions (layout shift risk)
  const noDims = findImagesMissingDimensions(iframeDoc);
  if (noDims.length > 2) {
    issues.push({
      severity: SEVERITY.WARNING,
      code: 'images_missing_dimensions',
      message: `${noDims.length} images are missing dimensions — content may shift as they load.`,
      // Action context — the modal will probe each src for intrinsic
      // dimensions at click time. We do NOT require naturalWidth>0 here
      // because lazy images can still report 0 at check time.
      images: noDims.map((img) => ({
        src: img.currentSrc || img.src,
        width: img.naturalWidth || 0,
        height: img.naturalHeight || 0,
      })).filter((it) => it.src),
    });
  }

  // 8. Non-lazy non-hero images (info only)
  const nonLazy = findNonLazyImages(iframeDoc);
  if (nonLazy.length > 3) {
    issues.push({
      severity: SEVERITY.INFO,
      code: 'non_lazy_images',
      message: `${nonLazy.length} images load eagerly. Lazy-loading non-hero images would speed up the first paint.`,
    });
  }

  const ok = issues.filter((i) => i.severity === SEVERITY.ERROR).length === 0;
  return { ok, summary: summarize(issues, layoutVersion, layoutMode), issues };
}

function summarize(issues, layoutVersion, layoutMode) {
  const errors = issues.filter((i) => i.severity === SEVERITY.ERROR).length;
  const warnings = issues.filter((i) => i.severity === SEVERITY.WARNING).length;
  if (errors === 0 && warnings === 0) return 'Looks good on mobile';
  const parts = [];
  if (errors > 0) parts.push(`${errors} issue${errors > 1 ? 's' : ''}`);
  if (warnings > 0) parts.push(`${warnings} thing${warnings > 1 ? 's' : ''} may need attention`);
  return parts.join(' · ');
}

export { SEVERITY };
